<template>

	<unauthed-panel :title="$t('titles.reset_your_password')">

		<p class="text-center mb-12">{{ $t('alerts.reset_password_instructions') }}</p>

		<v-form @submit.prevent="submit" novalidate>
			
			<o-text-field
				:label="$t('titles.username_or_email_address')"
				v-model.trim="v$.identifier.$model"
				:validator="v$.identifier"
				prepend-inner-icon="$user"
        autofocus
        required
        autocomplete="username"
			/>

			<o-text-field
				:label="$t('titles.verification_code')"
				v-model.trim="v$.verificationCode.$model"
				:validator="v$.verificationCode"
				prepend-inner-icon="$code"
        required
        autocomplete="off"
			/>

			<o-text-field
				:label="$t('titles.new_password')"
				type="password"
				:hint="$t('misc.password_requirements')"
				persistent-hint
				v-model.trim="v$.newPassword1.$model"
				:validator="v$.newPassword1"
				prepend-inner-icon="$password"
        required
        autocomplete="new-password"
			/>

			<o-text-field
				:label="$t('titles.repeat_new_password')"
				type="password"
				v-model.trim="v$.newPassword2.$model"
				:validator="v$.newPassword2"
				prepend-inner-icon="$password"
        required
        autocomplete="new-password"
			/>

			<div class="text-right mb-4">
				<router-link :to="{ name: 'login' }" >
					{{ $t('actions.go_back_to_login') }}
				</router-link>
			</div>

			<v-btn
				type="submit"
				block
				prepend-icon="$confirm"
				:loading="submitting"
				:disabled="submitting"
			>{{ $t('actions.reset_password') }}</v-btn>

		</v-form>
		
	</unauthed-panel>

</template>

<script setup lang="ts">

import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n }  from 'vue-i18n'
import { useTitle } from '@vueuse/core'
import store from '../../plugins/vuex'
import UnauthedPanel from '../UnauthedPanel.vue'
import NotificationService from '../../services/NotificationService'

// validation
import useVuelidate from '@vuelidate/core'
import { required } from '../../utils/i18n-validators'
import { helpers } from '@vuelidate/validators'
import passwordComplexity from '../../rules/passwordComplexity'

const router = useRouter()
const { t } = useI18n()

const title = useTitle()
title.value = t('titles.ocra_reset_your_password')

/**
 * Form state and rules
 */

interface FormState {
  identifier: string
  verificationCode: string
  newPassword1: string
  newPassword2: string
}

const formState = reactive<FormState>({
  identifier: '',
  verificationCode: '',
  newPassword1: '',
  newPassword2: '',
})

const sameAsNewPassword1 = (value: any) => {
  // don't bother validating unless both password fields have a value
  if (value === '' || formState.newPassword1 === '') {
    return true
  }  
  return value === formState.newPassword1
}

const formRules = {
  identifier: {
		required,
  },
  verificationCode: {
		required,
  },
  newPassword1: {
    required,
    passwordComplexity: helpers.withMessage(t('validations.fails_password_requirements'), passwordComplexity)
  },
  newPassword2: {
    required,
    sameAsNewPassword1: helpers.withMessage(t('validations.passwords_match'), sameAsNewPassword1),
  },
}

const v$ = useVuelidate(formRules, formState)

/**
 * Submit
 */

const submitting = ref<boolean>(false)

async function submit() {

  const isValid = await v$.value.$validate()

  if (!isValid) {
    return
  }

  submitting.value = true

  const payload = {
    username: formState.identifier,
    verificationCode: formState.verificationCode,
    newPassword: formState.newPassword1,
  }

  store.dispatch('auth/confirmForgotPassword', payload)
    .then(() => {
      NotificationService.success('Password changed')
      router.push({name: 'login'})
    })
    .catch((error: any) => {
    	console.error(error)
      NotificationService.error(error)
    })
    .finally(() => {
      submitting.value = false
    })

}
</script>
